import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X, ChevronDown, ChevronUp, CheckCircle } from 'lucide-react';

const STORAGE_KEY = 'td_consent';
const CONSENT_TTL_MS = 365 * 24 * 60 * 60 * 1000; // 12 months

export interface ConsentState {
  functional: boolean;
  analytics: boolean;
  acceptedAt: string;
}

export function getConsent(): ConsentState | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentState;
    if (Date.now() - new Date(parsed.acceptedAt).getTime() > CONSENT_TTL_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveConsent(functional: boolean, analytics: boolean) {
  const state: ConsentState = { functional, analytics, acceptedAt: new Date().toISOString() };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  return state;
}

export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [functional, setFunctional] = useState(true);
  const [analytics, setAnalytics] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (!getConsent()) {
      const timer = setTimeout(() => setVisible(true), 600);
      return () => clearTimeout(timer);
    }
  }, []);

  if (!visible) return null;

  const accept = (level: 'essential' | 'all' | 'custom') => {
    if (level === 'essential') saveConsent(false, false);
    else if (level === 'all') saveConsent(true, true);
    else saveConsent(functional, analytics);
    setSaved(true);
    setTimeout(() => setVisible(false), 1000);
  };

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6 pointer-events-none">
      <div
        className="max-w-2xl mx-auto bg-white border border-gray-200 rounded-2xl shadow-2xl
                   pointer-events-auto animate-[slideUp_0.3s_ease-out]"
        style={{ animation: 'slideUp 0.3s ease-out' }}
      >
        <style>{`@keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>

        {saved ? (
          <div className="flex items-center gap-3 px-5 py-4 text-green-700">
            <CheckCircle className="w-5 h-5 shrink-0" />
            <span className="text-sm font-medium">Preferences saved. Thank you.</span>
          </div>
        ) : (
          <>
            <div className="flex items-start gap-3 px-5 pt-5 pb-4">
              <Cookie className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 mb-1">
                  We value your privacy
                </p>
                <p className="text-xs text-gray-500 leading-relaxed">
                  We use essential storage for secure sign-in (via Microsoft Azure) and functional
                  storage to remember your preferences. We may also use analytics to improve the
                  platform. See our{' '}
                  <Link to="/cookies" className="text-blue-600 hover:underline">Cookie Policy</Link>
                  {' '}and{' '}
                  <Link to="/privacy" className="text-blue-600 hover:underline">Privacy Policy</Link>
                  {' '}for details.
                </p>
              </div>
              <button
                onClick={() => accept('essential')}
                className="p-1 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors shrink-0"
                title="Accept essential only and close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Manage options */}
            {expanded && (
              <div className="px-5 pb-4 space-y-3 border-t border-gray-100 pt-4">
                <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Manage cookies</p>

                <div className="space-y-3">
                  {/* Essential */}
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-800">Essential</p>
                      <p className="text-xs text-gray-500">Authentication tokens, security, consent record. Always active.</p>
                    </div>
                    <div className="w-9 h-5 bg-blue-600 rounded-full flex items-center justify-end pr-0.5 shrink-0 cursor-not-allowed">
                      <div className="w-4 h-4 bg-white rounded-full shadow" />
                    </div>
                  </div>

                  {/* Functional */}
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-800">Functional</p>
                      <p className="text-xs text-gray-500">Voice assistant preference, UI settings.</p>
                    </div>
                    <button
                      role="switch"
                      aria-checked={functional}
                      onClick={() => setFunctional(f => !f)}
                      className={`w-9 h-5 rounded-full flex items-center transition-colors shrink-0 ${
                        functional ? 'bg-blue-600 justify-end pr-0.5' : 'bg-gray-200 justify-start pl-0.5'
                      }`}
                    >
                      <div className="w-4 h-4 bg-white rounded-full shadow" />
                    </button>
                  </div>

                  {/* Analytics */}
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-800">Analytics</p>
                      <p className="text-xs text-gray-500">Anonymous usage statistics to help us improve the platform.</p>
                    </div>
                    <button
                      role="switch"
                      aria-checked={analytics}
                      onClick={() => setAnalytics(a => !a)}
                      className={`w-9 h-5 rounded-full flex items-center transition-colors shrink-0 ${
                        analytics ? 'bg-blue-600 justify-end pr-0.5' : 'bg-gray-200 justify-start pl-0.5'
                      }`}
                    >
                      <div className="w-4 h-4 bg-white rounded-full shadow" />
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center gap-2 px-5 pb-5 pt-2">
              <button
                onClick={() => accept('essential')}
                className="flex-1 sm:flex-none px-4 py-2 text-sm font-medium border border-gray-300
                           rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Essential only
              </button>

              <button
                onClick={() => setExpanded(e => !e)}
                className="flex-1 sm:flex-none flex items-center justify-center gap-1 px-4 py-2
                           text-sm font-medium border border-gray-300 rounded-lg text-gray-700
                           hover:bg-gray-50 transition-colors"
              >
                Manage
                {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
              </button>

              {expanded ? (
                <button
                  onClick={() => accept('custom')}
                  className="flex-1 sm:flex-none px-4 py-2 text-sm font-medium bg-blue-600
                             hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  Save preferences
                </button>
              ) : (
                <button
                  onClick={() => accept('all')}
                  className="flex-1 sm:flex-none px-4 py-2 text-sm font-medium bg-blue-600
                             hover:bg-blue-700 text-white rounded-lg transition-colors"
                >
                  Accept all
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
